import {
  Component,
  Inject,
  LOCALE_ID,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  ElementRef,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  AfterViewInit
} from '@angular/core';
import { Product, ProductElement } from '@models/product';
import { Vendor, VendorInventory } from '@models/vendor';
import { Store } from '@models/store';
import { User } from '@models/user';
import { ProductForm } from '@classes/product-form';
import { ProductSort } from '@classes/product-sort';
import { getInventoryStatusDisplay, getEffectiveStatusForSelect, normalizeStatus } from '@classes/inventory-aging';
import { ProductService } from '@services/product.service';
import { VendorService } from '@services/vendor.service';
import { StoreService } from '@services/store.service';
import { OrderService } from '@services/order.service';
import { ImageService } from '@services/image.service';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { FormGroup, FormBuilder, FormControl, FormArray } from '@angular/forms';
import { FileUploader } from 'ng2-file-upload';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { SaveComponent } from '@components/admin/dialog/save/save.component';
import { DeleteComponent } from '@components/admin/dialog/delete/delete.component';
import { ImageEditComponent } from '@components/admin/image/image-edit/image-edit.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { map, debounceTime, switchMap, mergeWith } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import * as _ from 'lodash';

const IMAGE_BATCH_SIZE = 100;
const LAST_INVOICE_DATE_BATCH_SIZE = 500;
const PROGRESSIVE_RENDER_THRESHOLD = 200;
const RENDER_CHUNK_SIZE = 150;

// Interfaces
export interface InventoryRow {
  product: Product;
  formGroup?: FormGroup;
}

interface TableColumnConfig {
  managedBoard: string[];
  parts: string[];
  default: string[];
}

interface UserPermissions {
  authorized: boolean;
  authorizedSendToStack: boolean;
}

interface StatusEditSession {
  originalStatus: string;
  userChanged: boolean;
}

interface FormConfig {
  typeOptions: Array<{viewValue: string; value: string}>;
  commissionOptions: Array<{viewValue: string; value: string}>;
  authorizedRoles: string[];
  authorizedSendToStackRoles: string[];
}

@Component({
  selector: 'app-inventory-base',
  templateUrl: './inventory-base.component.html',
  styleUrls: ['./inventory-base.component.css'],
  animations: [
  trigger('detailExpand', [
    state('collapsed, void', style({ height: '0px' })),
    state('expanded', style({ height: '*' })),
    transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])


  ]
})
export class InventoryBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public _vendorInventory: VendorInventory;

  @Input() public managedBoard: boolean;
	@Input() public borrowingView: boolean;
  @Input() public borrowingStore: string = null;
  @Input() public borrowingSelection: SelectionModel<FormGroup>;
  @Input() public differentStoreSelected = false;
  @Input() rentingView: boolean;
  @Input() rentingUser: User;
  @Input() rentingList: any[];
  @Input() allRentingQtyByProduct;
  @Input() updatedRentingList: any[];
  @Input()
  set vendor(val: Vendor) {
    this.cancelBackgroundLoads();
    this.dataSource = new MatTableDataSource<InventoryRow>();
    this.allProducts = [];
    this.clearProductSelection();
    this._vendor = val;
    this.productsForm.get('showStock').setValue('all');
  }

  @Output() selectRenting = new EventEmitter();

  @ViewChildren('productImage') productImages: QueryList<ElementRef>;
  @ViewChildren('formRow') rows: QueryList<ElementRef>;
	@ViewChild(MatTable, {static: false}) table: MatTable<FormControl>;
	@ViewChild('search', {static: false}) search: MatInput;
	@ViewChild(MatSort, {static: false}) sort: MatSort;
 	@ViewChild('statusSelect', {static: false}) set content(content: MatSelect) {
 		if (content !== undefined) {
 			this.statusSelect = content;
 			if (this.clickedStatus >= 0) {
 				this.statusSelect.open();
 				this.cdRef.detectChanges();
 			}
 		}
 	}

  public _vendor: Vendor;

  fieldOpen = false;
  clickedImage = false;
  index: number;
  expandedElement: InventoryRow | FormGroup = null;
  clickedStatus: number;
  storeOptions: any[];
 	statusSelect: MatSelect;
	products: Product[];
  dataSource = new MatTableDataSource<InventoryRow | FormGroup>();
  dataLoading = false;
  allProducts: Product[] = [];
  progressiveRenderCount = 0;
  progressiveRenderTotal = 0;
  private imageLoadRequestId = 0;
  private lastInvoiceDatesRequestId = 0;
  private chunkRenderHandle: number | null = null;
  private pendingProducts: Product[] | null = null;
  private renderedProductCount = 0;
  private statusEditSessions = new Map<string, StatusEditSession>();
  // Configuration objects
  private readonly TABLE_COLUMNS: TableColumnConfig = {
    managedBoard: ['images', 'model', 'color', 'size', 'status', 'type', 'cost', 'priceLink', 'retail', 'quantity', 'orderDate', 'borrow', 'rent', 'action'],
    parts: ['select', 'images', 'model', 'color', 'size', 'type', 'cost', 'priceLink', 'retail', 'quantity', 'borrow', 'rent', 'action', 'delete'],
    default: ['select', 'images', 'model', 'color', 'size', 'status', 'type', 'cost', 'priceLink', 'retail', 'quantity', 'borrow', 'rent', 'action', 'delete']
  };

  protected readonly FORM_CONFIG: FormConfig = {
    typeOptions: [
      {viewValue: 'RX', value: 'RX'},
      {viewValue: 'SUN', value: 'SUN'},
      {viewValue: 'OTHER', value: 'OTHER'}
    ],
    commissionOptions: [
      {viewValue: 'NONE', value: 'NONE'},
      {viewValue: 'SPIFF', value: 'SPIFF'},
      {viewValue: 'DRILLMOUNT', value: 'DRILLMOUNT'}
    ],
    authorizedRoles: ['MANAGER', 'OWNER/ADMIN', 'OPTICIAN/INVENTORY'],
    authorizedSendToStackRoles: ['MANAGER', 'OWNER/ADMIN', 'OPTICIAN/INVENTORY']
  };

  // Component properties
  displayedColumns: string[] = this.TABLE_COLUMNS.default;
  productsForm: FormGroup;
  productId: string;
  productIndex: number;
  editProduct: FormGroup;
  defaultSortColumn = '';
  uploaderProduct: FileUploader = new FileUploader({
    url: environment.base_url + '/image/product',
    itemAlias: 'photoProduct'
  });

  // Permissions
  permissions: UserPermissions = {
    authorized: false,
    authorizedSendToStack: false
  };

  // Template-accessible properties (getters for backward compatibility)
  get typeOptions() {
    return this.FORM_CONFIG.typeOptions;
  }

  get authorized() {
    return this.permissions.authorized;
  }

  get authorizedSendToStack() {
    return this.permissions.authorizedSendToStack;
  }
  selection: SelectionModel<FormGroup> = new SelectionModel<FormGroup>(true, []);
  selectedProductIds: Set<string> = new Set<string>();
  timestamp: number = new Date().getTime();
  imageUrls: Record<string, string> = {};
  showParts = false;
  user;
  currentStore: Store;
  searchControl: FormControl = new FormControl();
  showData: boolean = false;
  showAll: boolean = false;
  lodash = _;
  
  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public vendorService: VendorService,
    public storeService: StoreService,
    public auth: AuthService,
    public productService: ProductService,
    public orderService: OrderService,
    public imageService: ImageService,
    public fb: FormBuilder,
    public cdRef: ChangeDetectorRef,
    public dialog: MatDialog
  ) {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.storeOptions = this.storeService.stores;
    this.currentStore = this.auth.getStoreObject();
    this.initializePermissions();
    this.initializeForms();
    this.setupSearchSubscription();
  }

  private initializeForms(): void {
    this.productsForm = this.fb.group({
      productsFormArray: this.fb.array([]),
      search: '',
      showStock: 'all',
      multipleFields: this.fb.group({
        field: '',
        status: '',
        type: '',
        cost: '',
        retail: '',
        quantity: ''
      })
    });

    this.searchControl = this.productsForm.get('search') as FormControl;
  }

  private setupSearchSubscription(): void {
    const searchValueChange = this.searchControl.valueChanges;
    const searchValueChangeMid = searchValueChange.pipe(
      map(() => { this.dataLoading = true; })
    );
    const searchValueChangeEnd = searchValueChange.pipe(
      debounceTime(500),
      switchMap((changedValue) => this.filterProducts(changedValue))
    );

    searchValueChange.pipe(mergeWith(searchValueChangeMid, searchValueChangeEnd))
      .subscribe();
  }

  private initializePermissions(): void {
    this.permissions.authorized = this.FORM_CONFIG.authorizedRoles.includes(this.auth.currentUser.role);
    this.permissions.authorizedSendToStack = this.FORM_CONFIG.authorizedSendToStackRoles.includes(this.auth.currentUser.role);
  }

	ngOnInit() {
    this.user = this.auth.getUser();
	}

	ngAfterViewInit() {
  	this.setTableColumns();
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.cancelBackgroundLoads();
  }

  trackByProductId = (_index: number, row: InventoryRow | FormGroup): string => {
    return this.rowValue(row)?._id ?? String(_index);
  };

  rowValue(row: InventoryRow | FormGroup): Product {
    if (this.isInventoryRow(row)) {
      return (row.formGroup?.value ?? row.product) as Product;
    }
    return row.value as Product;
  }

  getStatusCellClass(row: InventoryRow | FormGroup): string {
    if (this.isEditing(row) && !this.showParts) {
      return this.rowValue(row).status;
    }
    return getInventoryStatusDisplay(this.getProductForAging(row), this._vendor).cssClass || '';
  }

  getStatusCellStyle(row: InventoryRow | FormGroup): Record<string, string> {
    if (this.isEditing(row) && !this.showParts) {
      return {};
    }
    const display = getInventoryStatusDisplay(this.getProductForAging(row), this._vendor);
    return display.backgroundColor
      ? { 'background-color': display.backgroundColor }
      : {};
  }

  private getProductForAging(row: InventoryRow | FormGroup): { status?: string; lastInvoiceDate?: number | string } {
    if (this.isInventoryRow(row)) {
      return {
        status: row.product.status,
        lastInvoiceDate: row.product.lastInvoiceDate,
      };
    }
    const values = this.rowValue(row);
    return {
      status: values.status,
      lastInvoiceDate: values.lastInvoiceDate,
    };
  }

  private syncStatusSelectForEdit(editForm: FormGroup, row: InventoryRow | FormGroup): void {
    const product = this.rowValue(row);
    const effectiveStatus = getEffectiveStatusForSelect(this.getProductForAging(row), this._vendor);

    this.statusEditSessions.set(String(product._id), {
      originalStatus: normalizeStatus(product.status),
      userChanged: false,
    });

    editForm.get('status').setValue(effectiveStatus, { emitEvent: false });
  }

  private applyStatusBeforeSave(editProduct: FormGroup): void {
    const session = this.statusEditSessions.get(String(editProduct.value._id));
    if (!session) {
      return;
    }

    if (session.userChanged) {
      editProduct.patchValue({ status: editProduct.get('status').value }, { emitEvent: false });
    } else {
      editProduct.patchValue({ status: session.originalStatus }, { emitEvent: false });
    }

    this.statusEditSessions.delete(String(editProduct.value._id));
  }

  private clearStatusEditSession(productId?: string): void {
    if (productId) {
      this.statusEditSessions.delete(String(productId));
      return;
    }
    this.statusEditSessions.clear();
  }

  isEditing(row: InventoryRow | FormGroup): boolean {
    return String(this.productId) === String(this.rowValue(row)._id);
  }

  isInventoryRow(row: InventoryRow | FormGroup): row is InventoryRow {
    return !!(row as InventoryRow).product;
  }

  ensureFormGroup(row: InventoryRow | FormGroup): FormGroup {
    if (this.isInventoryRow(row)) {
      if (!row.formGroup) {
        row.formGroup = ProductForm.initProductFormGroup(row.product);
        this.syncProductsFormArray();
      }
      return row.formGroup;
    }
    return row;
  }

  getVendorRef(product: Product): Vendor | null {
    const vendor = product.vendor;
    return typeof vendor === 'object' ? vendor : null;
  }

  isRowSelected(row: InventoryRow | FormGroup): boolean {
    const productId = this.rowValue(row)._id;
    if (productId && this.selectedProductIds.has(productId)) {
      return true;
    }
    const formGroup = this.isInventoryRow(row) ? row.formGroup : row;
    return formGroup ? this.selection.isSelected(formGroup) : false;
  }

  private toInventoryRow(product: Product): InventoryRow {
    return { product };
  }

  private cancelBackgroundLoads(): void {
    this.imageLoadRequestId++;
    this.lastInvoiceDatesRequestId++;
    this.cancelProgressiveRender();
  }

  private cancelProgressiveRender(): void {
    if (this.chunkRenderHandle != null) {
      cancelAnimationFrame(this.chunkRenderHandle);
      this.chunkRenderHandle = null;
    }
    this.pendingProducts = null;
    this.renderedProductCount = 0;
    this.progressiveRenderCount = 0;
    this.progressiveRenderTotal = 0;
  }

  private loadProducts(products: Product[], showStock = 'all'): void {
    this.cancelBackgroundLoads();
    this.imageUrls = {};
    this.allProducts = products;
    this.productsForm.get('showStock').setValue(showStock);

    if (products.length <= PROGRESSIVE_RENDER_THRESHOLD) {
      this.renderAllProducts(products);
      return;
    }

    this.dataLoading = true;
    this.progressiveRenderTotal = products.length;
    this.progressiveRenderCount = 0;
    this.renderedProductCount = 0;
    this.pendingProducts = products;
    this.dataSource = new MatTableDataSource<InventoryRow>([]);
    this.syncProductsFormArray();
    this.renderNextChunk();
  }

  private renderAllProducts(products: Product[]): void {
    this.dataLoading = true;
    const rows = products.map((product) => this.toInventoryRow(product));
    this.dataSource = new MatTableDataSource<InventoryRow>(rows);
    this.finishProductLoad(rows, products);
  }

  private renderNextChunk(): void {
    if (!this.pendingProducts) {
      return;
    }

    const nextCount = Math.min(
      this.renderedProductCount + RENDER_CHUNK_SIZE,
      this.pendingProducts.length
    );
    const newRows = this.pendingProducts
      .slice(this.renderedProductCount, nextCount)
      .map((product) => this.toInventoryRow(product));

    if (this.renderedProductCount === 0) {
      this.dataSource.data = newRows;
    } else {
      this.dataSource.data.push(...newRows);
      this.table?.renderRows();
    }

    this.renderedProductCount = nextCount;
    this.progressiveRenderCount = nextCount;

    if (this.renderedProductCount < this.pendingProducts.length) {
      this.chunkRenderHandle = requestAnimationFrame(() => this.renderNextChunk());
      return;
    }

    const products = this.pendingProducts;
    const rows = this.dataSource.data as InventoryRow[];
    this.pendingProducts = null;
    this.chunkRenderHandle = null;
    this.finishProductLoad(rows, products);
  }

  private finishProductLoad(rows: InventoryRow[], products: Product[]): void {
    this.syncProductsFormArray();
    this.syncSelectionFromSelectedIds();
    ProductSort.setInventoryRowSorter(
      this.dataSource as MatTableDataSource<InventoryRow>,
      this.sort,
      this._vendor
    );
    this.dataLoading = false;
    this.progressiveRenderCount = 0;
    this.progressiveRenderTotal = 0;
    this.cdRef.detectChanges();

    this.loadImagesInBackground(rows);
    this.loadLastInvoiceDatesInBackground(products);
  }

  getPreviewImageId(row: InventoryRow | FormGroup): string | null {
    const images = this.rowValue(row).images;
    return images?.length ? images[0] : null;
  }

  getPreviewImageUrl(row: InventoryRow | FormGroup): string | null {
    const imageId = this.getPreviewImageId(row);
    return imageId ? this.imageUrls[imageId] ?? null : null;
  }

  private syncProductsFormArray(): void {
    const formGroups = this.dataSource.data
      .map((row) => (this.isInventoryRow(row) ? row.formGroup : row))
      .filter((formGroup): formGroup is FormGroup => !!formGroup);
    this.productsForm.setControl('productsFormArray', this.fb.array(formGroups));
  }

  setProductForms(products: Product[], showStock = 'all'): Observable<null> {
    this.cancelBackgroundLoads();
    this.imageUrls = {};
    this.allProducts = products;
    const formGroups = products.map((product) => ProductForm.initProductFormGroup(product));
    this.productsForm.setControl('productsFormArray', this.fb.array(formGroups));
    this.productsForm.get('showStock').setValue(showStock);
    this.dataSource = new MatTableDataSource<InventoryRow | FormGroup>(formGroups);
    ProductSort.setSorter(this.dataSource as MatTableDataSource<FormGroup>, this.sort, this._vendor);
    this.dataLoading = false;
    this.cdRef.detectChanges();
    return new Observable((observer) => {
      observer.next(null);
      observer.complete();
    });
  }

  private loadVendorInventory(filterString: string, stockOption?: string): void {
    const option = stockOption ?? this.productsForm.get('showStock').value;
    if (option === 'parts') {
      this.filterParts();
      return;
    }
    this.dataLoading = true;
    let vendorInventory = this.vendorService.getVendorInventory(this._vendor, filterString, option);
    if (this._vendor.differentStore) {
      vendorInventory = this.vendorService.getVendorInventory(this._vendor, filterString, option, this._vendor.differentStore);
    }
    vendorInventory.subscribe((vendorInventories: VendorInventory[]) => {
      const _vendorInventory = vendorInventories[0];
      _vendorInventory.products = _vendorInventory.products.map(product => {
        product.borrowing = _(product.borrowing).groupBy('store').map((objs, key) => ({
          store: key,
          quantity: _.sumBy(objs, 'quantity')
        })).value();
        return product;
      });
      _vendorInventory.products = this.mergeSelectedProducts(_vendorInventory.products);
      this._vendorInventory = _vendorInventory;
      this.loadProducts(this._vendorInventory.products, option);
    });
  }

  private loadRowImages(row: InventoryRow): void {
    this.loadImagesInBackground([row]);
  }

  private loadImagesInBackground(rows: InventoryRow[]): void {
    const requestId = ++this.imageLoadRequestId;
    const missingIds: string[] = [];

    rows.forEach((row) => {
      (row.product.images || []).forEach((id) => {
        if (id && !this.imageUrls[id] && !missingIds.includes(id)) {
          missingIds.push(id);
        }
      });
    });

    if (!missingIds.length) {
      return;
    }

    const batches: string[][] = [];
    for (let i = 0; i < missingIds.length; i += IMAGE_BATCH_SIZE) {
      batches.push(missingIds.slice(i, i + IMAGE_BATCH_SIZE));
    }

    let batchIndex = 0;
    const processNextBatch = (): void => {
      if (batchIndex >= batches.length || requestId !== this.imageLoadRequestId) {
        if (requestId === this.imageLoadRequestId) {
          this.cdRef.detectChanges();
        }
        return;
      }

      const batch = batches[batchIndex++];
      this.imageService
        .getBulkThumbnailUrls(batch, this.imageService.getThumbnailWidth('product'))
        .subscribe((urls: Record<string, string>) => {
          if (requestId !== this.imageLoadRequestId) {
            return;
          }
          this.imageUrls = { ...this.imageUrls, ...urls };
          processNextBatch();
        });
    };

    processNextBatch();
  }

  private loadLastInvoiceDatesInBackground(products: Product[]): void {
    const productIds = products.map((product) => product._id).filter(Boolean);
    if (!productIds.length) {
      return;
    }

    const requestId = ++this.lastInvoiceDatesRequestId;
    const batches: string[][] = [];
    for (let i = 0; i < productIds.length; i += LAST_INVOICE_DATE_BATCH_SIZE) {
      batches.push(productIds.slice(i, i + LAST_INVOICE_DATE_BATCH_SIZE));
    }

    let batchIndex = 0;
    const processNextBatch = (): void => {
      if (batchIndex >= batches.length || requestId !== this.lastInvoiceDatesRequestId) {
        if (requestId === this.lastInvoiceDatesRequestId) {
          this.cdRef.detectChanges();
        }
        return;
      }

      this.vendorService.getProductLastInvoiceDates(batches[batchIndex++]).subscribe((dates) => {
        if (requestId !== this.lastInvoiceDatesRequestId) {
          return;
        }
        this.applyLastInvoiceDates(dates, false);
        processNextBatch();
      });
    };

    processNextBatch();
  }

  private applyLastInvoiceDates(dates: Record<string, string | number>, detectChanges = true): void {
    if (!dates || !Object.keys(dates).length) {
      return;
    }

    const productById = new Map(this.allProducts.map((product) => [product._id, product]));

    Object.entries(dates).forEach(([productId, lastInvoiceDate]) => {
      const product = productById.get(productId);
      if (product) {
        product.lastInvoiceDate = lastInvoiceDate;
      }
    });

    if (detectChanges) {
      this.cdRef.detectChanges();
    }
  }

  private setTableColumns(): void {
    this.showParts = false;
    this.defaultSortColumn = '';

    if (this.managedBoard) {
      this.displayedColumns = this.getManagedBoardColumns();
    } else if (this.productsForm.get('showStock').value === 'parts') {
      this.showParts = true;
      this.displayedColumns = this.TABLE_COLUMNS.parts;
    } else {
      this.displayedColumns = this.getDefaultColumns();
    }
  }

  private getManagedBoardColumns(): string[] {
    return this.user.role === 'RENTAL'
      ? ['images', 'model', 'color', 'size', 'status', 'type', 'quantity', 'orderDate', 'rent', 'action']
      : this.TABLE_COLUMNS.managedBoard;
  }

  private getDefaultColumns(): string[] {
    return this.user.role === 'RENTAL'
      ? ['select', 'images', 'model', 'color', 'size', 'status', 'type', 'quantity', 'rent', 'action', 'delete']
      : this.TABLE_COLUMNS.default;
  }

  getVendorInventory(filterString?: string) {
    this.loadVendorInventory(filterString ?? '');
  }

  private clearProductSelection(): void {
    this.selection.clear();
    this.selectedProductIds.clear();
    this.showAll = false;
    this.searchControl?.setValue('', { emitEvent: false });
  }

  private mergeSelectedProducts(products: Product[]): Product[] {
    if (!this.selectedProductIds.size) {
      return products;
    }

    const productMap = new Map<string, Product>();
    products.forEach(product => productMap.set(product._id, product));

    this.dataSource.data.forEach((row) => {
      const selectedProduct = this.rowValue(row);
      if (selectedProduct?._id && this.selectedProductIds.has(selectedProduct._id) && !productMap.has(selectedProduct._id)) {
        productMap.set(selectedProduct._id, selectedProduct);
      }
    });

    return Array.from(productMap.values());
  }



  filterProductStock() {
    this.dataLoading = true;
    this.getVendorInventory(this.searchControl.value);
  }

  filterParts() {
    this.orderService.getPartsOrders(this._vendor._id).subscribe((partsOrders: any[]) => {
      this.setTableColumns();
      const products = partsOrders.flatMap(order => order.products);
      this.loadProducts(products, 'parts');
    });
  }

  filterInventory() {
    this.loadProducts(this._vendorInventory.products, this.productsForm.get('showStock').value);
  }

  private syncSelectionFromSelectedIds(): void {
    this.selection.clear();
    this.dataSource.data.forEach((row) => {
      const productId = this.rowValue(row)._id;
      if (productId && this.selectedProductIds.has(productId)) {
        this.selection.select(this.ensureFormGroup(row));
      }
    });
  }

  searchControlEnter($event) {
    if (this.searchControl.value == '') {
      this.showEverything();
    }
  }

  showEverything() {
    this.showAll = true;
    this.productsForm.get('showStock').setValue('stock');
    this.loadVendorInventory('', 'stock');
  }

  showAllFrames() {
    this.showAll = true;
    this.productsForm.get('showStock').setValue('all');
    this.loadVendorInventory('', 'all');
  }

  async filterProducts(filterValue: string) {
    if (filterValue.length < 1) {
      this.showAll = false;
      if (!this.showAll) {
        this.cancelBackgroundLoads();
        this.allProducts = [];
        this.dataSource = new MatTableDataSource<InventoryRow>();
        this.syncProductsFormArray();
        this.dataLoading = false;
      }
    } else {
      this.dataLoading = true;
      this.cdRef.detectChanges();
      this.getVendorInventory(filterValue);
    }
    return true;
  }

  addProductFormItem(product: Product): InventoryRow {
    const row: InventoryRow = {
      product,
      formGroup: ProductForm.initProductFormGroup(product),
    };
    return row;
  }

  getStoreObject(store: Store): Store {
    return this.storeService.getStoreObjectFromId(store._id);
  }

  clickRow($event: Event, row: InventoryRow | FormGroup, index) {
    if (this.user && this.user.role === 'RENTAL') {
      return;
    }
    if (this.borrowingView) {
      return;
    }

    const target = $event.target as HTMLElement;
    const rowId = this.rowValue(row)._id;
    const isSameRow = String(this.productId) === String(rowId);
    const isInteractiveClick = !!target.closest(
      'mat-form-field, mat-select, mat-option, textarea, input, app-autocomplete-vendor-products, app-images, button, mat-checkbox, mat-icon, mat-menu, .mat-menu-panel'
    );

    if (isInteractiveClick && isSameRow) {
      return;
    }

    this.clickedImage = false;

    if (isSameRow) {
      this.resetProductState();
    } else {
      const editForm = this.ensureFormGroup(row);
      if (this.isInventoryRow(row)) {
        this.loadRowImages(row);
      }
      this.productId = rowId;
      this.productIndex = index;
      this.index = index;
      this.expandedElement = row;
      this.editProduct = editForm;
      this.syncStatusSelectForEdit(editForm, row);
      this.syncProductsFormArray();
    }

    this.fieldOpen = false;
    $event.preventDefault();
    $event.stopPropagation();
    this.cdRef.detectChanges();
  }

  @HostListener('document:click', ['$event']) clickOutside ($event) {
    const target = $event.target as HTMLElement;
    if (target.closest('mat-table')) {
      return;
    }
    this.clickedImage = false;
    if (!this.fieldOpen) {
      this.resetProductState();
    } else {
      this.fieldOpen = false;
    }
  }

  saveEditedProduct($event, editProduct: FormGroup, index, ordered = false): void {
    if (!ordered) {
      this.setFieldState(true, $event);
    }

    if (this.isNewProduct()) {
      this.handleNewProduct(editProduct, ordered);
    } else {
      this.handleExistingProduct(editProduct, ordered);
    }
  }

  private setFieldState(open: boolean, event?: Event): void {
    this.fieldOpen = open;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private isNewProduct(): boolean {
    return this.productId === 'NEW';
  }

  private handleNewProduct(editProduct: FormGroup, ordered: boolean): void {
    if (!this.editProduct.value.model?.trim()) {
      return;
    }

    this.prepareNewProduct(editProduct);
    const editImages = [...(editProduct.value.images || [])];
    editProduct.value.images = [];

    this.productService.addProduct(editProduct.value).subscribe(res => {
      if (res.success) {
        this.processSuccessfulNewProduct(editProduct, res.product, editImages, ordered);
      } else {
        this.handleSaveError(editProduct, res.message);
      }
    });
  }

  private prepareNewProduct(editProduct: FormGroup): void {
    editProduct.value._id = null;
    editProduct.value.vendor = this._vendor._id;
    editProduct.value.model = editProduct.value.model.toUpperCase();
    editProduct.value.images = Array.isArray(editProduct.value.images) ? editProduct.value.images : [];
  }

  private processSuccessfulNewProduct(editProduct: FormGroup, product: Product, editImages: any[], ordered: boolean): void {
    const newProductId = product._id;
    editProduct.get('_id').setValue(newProductId);

    if (ordered) {
      this.createStockOrder(editProduct);
    }

    editProduct.markAsPristine();
    this.processProductImages(newProductId, editImages);
    this.resetProductState();
    this.cdRef.detectChanges();
  }

  private createStockOrder(editProduct: FormGroup): void {
    const productElement = this.createProductElement(editProduct, 1);
    this.orderService.addStockOrder(productElement, editProduct.value.type, 'AWAITING FRAME')
      .subscribe(order => console.log('Stock order created:', order));
  }

  private createProductElement(editProduct: FormGroup, quantity: number): ProductElement {
    const productElement = new ProductElement();
    productElement.product = editProduct.value;
    productElement.type = editProduct.value.type;
    productElement.quantity = quantity;
    productElement.retail = editProduct.value.retail;
    productElement.cost = editProduct.value.cost;
    productElement.store = this.auth.getStoreObject();
    return productElement;
  }

  private processProductImages(productId: string, images: any[]): void {
    images.forEach(image => {
      this.productService.addImage(productId, image).subscribe(product => {
        this.editProduct?.get('images').setValue(product.images);
        this.cdRef.detectChanges();
      });
    });
  }

  private handleExistingProduct(editProduct: FormGroup, ordered: boolean): void {
    if (editProduct.value.status === 'return') {
      editProduct.get('status').setValue('white');
    }

    if (ordered) {
      this.createStockOrder(editProduct);
    }

    this.saveProduct(editProduct);
  }

  private handleSaveError(editProduct: FormGroup, message: string): void {
    editProduct.value._id = 'NEW';
    this.dialog.open(SaveComponent, {
      width: '250px',
      data: { message }
    });
  }

  private resetProductState(): void {
    if (this.productId) {
      this.clearStatusEditSession(String(this.productId));
    }
    this.productId = '';
    this.editProduct = null;
    this.productIndex = null;
    this.expandedElement = null;
  }


  saveProduct(editProduct: FormGroup) {
    this.applyStatusBeforeSave(editProduct);
    this.productService.updateProduct(editProduct.value._id, editProduct.value)
      .subscribe(res => {
      	if (res.success) {
					editProduct.markAsPristine();
          this.syncRowProduct(editProduct);
	        this.productId = '';
	        this.editProduct = null;
	        this.productIndex = null;
					this.expandedElement = null;
	        // this.getVendorProducts();


          if (editProduct.value.return) {
            const productElement = new ProductElement();
            productElement.product = editProduct.value;
            productElement.type = editProduct.value.type;
            productElement.quantity = editProduct.value.quantity;
            productElement.retail = editProduct.value.retail;
            productElement.cost = editProduct.value.cost;
            productElement.store = this.auth.getStoreObject();

            this.orderService.addReturnOrder(productElement, editProduct.value.type);
          }

	    		this.cdRef.detectChanges();
      	} else {
        	const dialogRef = this.dialog.open(SaveComponent, {
			      width: '250px',
			      data: { message: res['message'] }
			    });
			  	dialogRef.afterClosed().subscribe(result => {});
      	}
    	});
  }


  deleteProduct($event, row: InventoryRow, index) {
    $event.preventDefault();
    $event.stopPropagation();
    const editProduct = this.ensureFormGroup(row);
  	const dialogRef = this.dialog.open(DeleteComponent, {
      width: '250px',
      data: { message: 'DELETE PRODUCT?' }
    });
  	dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const dataIndex = this.dataSource.data.findIndex(d => d === row);
		    if (editProduct.value._id === 'NEW' || !editProduct.value._id) {
          this.removeProduct(dataIndex, row);
		    } else {
		      this.productService.deleteProduct(editProduct.value._id).subscribe(product => {
	          this.removeProduct(dataIndex, row);
	        });
		    }
      }
    });
  }

  removeProduct(dataIndex, row?: InventoryRow) {
    this.removeProductRow(dataIndex, row);
    this.productId = '';
    this.editProduct = null;
    this.productIndex = null;
    this.expandedElement = null;
  }

  getTimestamp(timestamp: Date) {
    return timestamp.getTime();
  }

  prepareSaveProduct(editProduct) {
    const product = new Product();
    product._id = editProduct.value._id;
    product.model = editProduct.value.name;
    return product;
  }

  removeProductRow(dataIndex: number, row?: InventoryRow | FormGroup) {
    if (row) {
      this.selection.deselect(this.ensureFormGroup(row));
    }
    const nextData = [...this.dataSource.data];
    nextData.splice(dataIndex, 1);
    this.dataSource.data = nextData;
    const productId = row ? this.rowValue(row)._id : null;
    if (productId) {
      this.allProducts = this.allProducts.filter((product) => product._id !== productId);
      this.selectedProductIds.delete(productId);
    }
    this.syncProductsFormArray();
    this.cdRef.detectChanges();
  }

  addProduct($event) {
    this.fieldOpen = true;
    const newProduct = new Product();
    newProduct._id = 'NEW';
    newProduct.type = 'RX';
    newProduct.status = this._vendor.defaultManagedBoardStatus;
    newProduct.vendor = this._vendor;
    newProduct.quantity = 0;
    newProduct.store = this.auth.getStoreObject();
    const row = this.addProductFormItem(newProduct);
    this.allProducts = [...this.allProducts, newProduct];
    this.dataSource.data = [...this.dataSource.data, row];

    this.editProduct = row.formGroup;
    if (this.user.role === 'RENTAL') {
      this.editProduct = null;
    }

    this.productId = newProduct._id;
    this.productIndex = this.dataSource.data.length - 1;
    $event.preventDefault();
    $event.stopPropagation();
    this.syncProductsFormArray();
    this.cdRef.detectChanges();
  }

  submit() {
    for (let x = 0; x < this.productsForm.controls['productsFormArray']['length']; x++) {
      const form = this.productsForm.controls['productsFormArray']['controls'][x];
    }
  }

  clickField($event, product, index) {
    this.fieldOpen = true;
  }

  clickImage($event, product, index) {
    this.clickedImage = true;
    console.log($event);
    console.log(product);
    console.log(index);
    // $event.preventDefault();
    // $event.stopPropagation();
  }

	clickStatus($event, product, index) {
    if (this.clickedStatus !== index) {
			$event.preventDefault();
    	$event.stopPropagation();
    }
    this.clickedStatus = index;
  }

  changeStatus($event, product, index) {
  	if (this.productId !== 'NEW') {
    	this.clickedStatus = -1;
	    const saveProduct = this.productsForm.value.productsFormArray[index];
 			this.statusSelect.close();
 			this.cdRef.detectChanges();
		  this.saveEditedProduct($event, saveProduct, index);
		}
  }

  changeProductStatus($event, product: FormGroup, index): boolean {
    const session = this.statusEditSessions.get(String(product.value._id));
    if (session) {
      session.userChanged = true;
    }
    if ($event.value === 'black') {
      product.get('return').setValue(true);
    } else {
      product.get('return').setValue(false);
    }
    return this.changeProductType(product);
  }

  changeProductType(product: FormGroup): boolean {
    // if ($event.source.selected && $event.isUserInput) {
      this.changeCost(product);
    // }
    return true;
  }

  togglePriceLinkForRow($event, row: InventoryRow, index) {
    if (!this.isEditing(row)) {
      return;
    }
    this.togglePriceLink($event, this.ensureFormGroup(row), index);
  }

  togglePriceLink($event, product: FormGroup, index) {
    this.fieldOpen = true;
    if (this.permissions.authorized) {
      const saveProduct = ProductForm.togglePriceLink(this.productsForm, product, this.editProduct, this._vendor, index);

      if (saveProduct) {
        this.saveProduct(product);
      }
    }
    $event.preventDefault();
    $event.stopPropagation();
  }

  isRowBorrowSelected(row: InventoryRow | FormGroup): boolean {
    const formGroup = this.isInventoryRow(row) ? row.formGroup : row;
    return formGroup ? this.borrowingSelection.isSelected(formGroup) : false;
  }

  private syncRowProduct(editProduct: FormGroup): void {
    const row = this.dataSource.data.find((item) => {
      if (this.isInventoryRow(item)) {
        return item.formGroup === editProduct || item.product._id === editProduct.value._id;
      }
      return item === editProduct || item.value._id === editProduct.value._id;
    });
    if (!row) {
      return;
    }
    const updated = Object.assign(new Product(), this.rowValue(row as InventoryRow | FormGroup), editProduct.getRawValue());
    if (this.isInventoryRow(row)) {
      row.product = updated;
    } else {
      row.patchValue(updated);
    }
    const productIndex = this.allProducts.findIndex((product) => product._id === updated._id);
    if (productIndex >= 0) {
      this.allProducts[productIndex] = updated;
    }
  }

  cropImage(imageId) {
    const dialogRef = this.dialog.open(ImageEditComponent, {
      width: '600px',
      height: '500px',
      data: { image: imageId, message: '' }
    });
    dialogRef.afterClosed().subscribe(result => {});
    dialogRef.componentInstance.imageChange.subscribe(img => {
      // image.date = img.date;
    });
  }

  calculateProductPrice(product) {
    return ProductForm.calculateProductPrice(product);
  }

  changeRetail(product: FormGroup) {
    ProductForm.changeRetail(product, this._vendor);
  }

  changeCost(product: FormGroup) {
    ProductForm.changeCost(product, this._vendor);
  }

  updateImages(images, row: InventoryRow | FormGroup) {
    const productForm = this.ensureFormGroup(row);
    productForm.get('images').setValue(images);
    if (this.isInventoryRow(row)) {
      row.product.images = images;
    }
    this.cdRef.detectChanges();
    if (productForm.value._id !== 'NEW') {
      this.productService.updateImages(productForm.value._id, images).subscribe((res) => {
      });
    }
  }








  checkboxLabel(row?: FormGroup, index?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${index + 1}`;
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numRows > 0 && numSelected === numRows;
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectedProductIds.clear();
    } else {
      this.dataSource.data.forEach((row) => {
        const formGroup = this.ensureFormGroup(row);
        this.selection.select(formGroup);
        const productId = this.rowValue(row)._id;
        if (productId) {
          this.selectedProductIds.add(productId);
        }
      });
    }
    this.syncProductsFormArray();
    this.cdRef.detectChanges();
  }

  toggleSelect(vendor, row: InventoryRow | FormGroup) {
    const order = this.ensureFormGroup(row);
    const productId = order?.value?._id;
    if (this.selection.isSelected(order)) {
      this.selection.deselect(order);
      if (productId) {
        this.selectedProductIds.delete(productId);
      }
    } else {
      this.selection.select(order);
      if (productId) {
        this.selectedProductIds.add(productId);
      }
    }

    // Re-apply active search so deselected rows can drop out immediately.
    if (this.searchControl?.value?.trim()) {
      this.getVendorInventory(this.searchControl.value.trim());
    }
    this.syncProductsFormArray();
    this.cdRef.detectChanges();
  }

  toggleSelectBorrow(vendor, row: InventoryRow | FormGroup) {
    this.borrowingSelection.toggle(this.ensureFormGroup(row));
    this.cdRef.detectChanges();
  }

  private getTotalSourceProducts(): Product[] {
    if (this.allProducts.length) {
      return this.allProducts;
    }
    return this._vendorInventory?.products ?? [];
  }

  getTotalCost() {
    const products = this.getTotalSourceProducts();
    if (products.length) {
      return products
        .filter((product) => product.quantity > 0)
        .reduce((acc, product) => acc + product.cost * product.quantity, 0);
    }
    return 0;
  }

  getTotalQty() {
    const products = this.getTotalSourceProducts();
    if (products.length) {
      return products
        .filter((product) => product.quantity > 0)
        .map((product) => product.quantity)
        .reduce((acc, value) => acc + (Number(value) || 0), 0);
    }
    return 0;
  }

  borrowMultipleProducts(storeObject: Store) {
    console.log(storeObject);

  }

  updateProducts() {
    if (this._vendor) {
      this.dataLoading = true;
      this.loadVendorInventory(this.searchControl?.value?.trim() || '');
    }
  }

  onSelectRenting(event: MatCheckboxChange, productId: string, vendorId: string) {
    this.selectRenting.next({
      checked: event.checked,
      productId,
      vendorId
    });
  }

  getRenting(productId: string) {
    return this.rentingList.find(renting => renting.product._id === productId);
  }

  getUpdatedRenting(productId: string) {
    return this.updatedRentingList.find(renting => renting._id === productId);
  }

  getBorrowingSum(borrowingList) {
    let sum = 0;
    for (const borrowing of borrowingList) {
      sum += borrowing.quantity;
    }
    return sum;
  }
}
